/**
 *  Component: Competencies
 * */
import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { checkIntentLandingPagesFeatureEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { MESH_TAG_TYPES } from 'constants/ProjectConstants';

import { getCompetencySettings } from 'services/competencies';

import CompetencyByFunctionAndRole from './CompetencieByFunctionAndRole/CompetencyByFunctionAndRole';
import CompetencyList from './CompetenciesByFunction/CompetencyList';
import OrgWideCompetencies from './CompetenciesForOrg/OrgWideCompetencies';
import { CompetencySettings } from './CompetencySettings/CompetencySettings';
import { CompetencySettingsRow } from './common/CommonComponents';
import { setViewType, useCompetencySettings } from './competencySettingsSlice';
import { CompetenciesUploadSidePanel } from './sidepanel/CompetenciesUploadSidePanel';

export const COMPETENCY_SETTINGS_TYPE = {
  ORG: 'ORG',
  FUNCTION: 'FUNCTION',
  ROLE: 'ROLE',
  DISABLED: 'DISABLED',
};

const Competencies = () => {
  const tagType = MESH_TAG_TYPES[2];

  const { viewType } = useCompetencySettings();
  const [loadingViewType, setLoadingViewType] = useState(true);
  const [showSettingsScreen, setShowSettingsScreen] = useState(false);

  const dispatch = useDispatch();

  // sidepanel
  const [competencyUploadSidePanel, setCompetencyUploadSidePanel] = useState(false);

  const openCompetencyUploadSidePanel = useCallback(() => {
    setCompetencyUploadSidePanel(true);
  }, [setCompetencyUploadSidePanel]);

  const closeCompetencyUploadSidePanel = useCallback(() => {
    setCompetencyUploadSidePanel(false);
  }, [setCompetencyUploadSidePanel]);

  const fetchSettings = () => {
    getCompetencySettings()
      .then((res) => {
        dispatch(setViewType(res.viewType));
        if (res.viewType === COMPETENCY_SETTINGS_TYPE.DISABLED) {
          setShowSettingsScreen(true);
        }
      })
      .catch((err) => {
        console.error('Error fetching competency settings : ', err);
      })
      .finally(() => {
        setLoadingViewType(false);
      });
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const getCompetencyScreen = ({ viewType, showSettingsScreen }) => {
    if (!viewType || showSettingsScreen) {
      return <CompetencySettings setShowSettingsScreen={setShowSettingsScreen} />;
    }

    if (viewType === COMPETENCY_SETTINGS_TYPE.ORG) {
      return (
        <Container>
          <OrgWideCompetencies
            key={tagType.value}
            tagType={tagType.value}
            singularTagTypeLabel={tagType.singularLabel}
            tagTypeLabel={tagType.label}
            tagLabel={tagType.tagLabel}
            isEnterpriseAccessOnly={tagType.isEnterpriseAccessOnly}
            allowDescriptionNewline
            setShowSettingsScreen={setShowSettingsScreen}
            openCompetencyUploadSidePanel={openCompetencyUploadSidePanel}
          />
          {!checkNewNavEnabled() && <CompetencySettingsRow setShowSettingsScreen={setShowSettingsScreen} />}
        </Container>
      );
    }

    if (viewType === COMPETENCY_SETTINGS_TYPE.FUNCTION) {
      return (
        <Container>
          <CompetencyList
            key={tagType.value}
            tagType={tagType.value}
            singularTagTypeLabel={tagType.singularLabel}
            tagTypeLabel={tagType.label}
            tagLabel={tagType.tagLabel}
            isEnterpriseAccessOnly={tagType.isEnterpriseAccessOnly}
            allowDescriptionNewline
            setShowSettingsScreen={setShowSettingsScreen}
            openCompetencyUploadSidePanel={openCompetencyUploadSidePanel}
          />
          {!checkNewNavEnabled() && <CompetencySettingsRow setShowSettingsScreen={setShowSettingsScreen} />}
        </Container>
      );
    }

    if (viewType === COMPETENCY_SETTINGS_TYPE.ROLE) {
      return (
        <Container>
          <CompetencyByFunctionAndRole
            key={tagType.value}
            tagType={tagType.value}
            singularTagTypeLabel={tagType.singularLabel}
            tagTypeLabel={tagType.label}
            tagLabel={tagType.tagLabel}
            isEnterpriseAccessOnly={tagType.isEnterpriseAccessOnly}
            allowDescriptionNewline
            setShowSettingsScreen={setShowSettingsScreen}
            originalTagType={tagType}
            openCompetencyUploadSidePanel={openCompetencyUploadSidePanel}
          />
          {!checkNewNavEnabled() && <CompetencySettingsRow setShowSettingsScreen={setShowSettingsScreen} />}
        </Container>
      );
    }
  };

  const CompetenciesScreen = getCompetencyScreen({ viewType, showSettingsScreen });

  if (loadingViewType || !CompetenciesScreen) {
    return null;
  }

  return (
    <div
      style={{
        padding: checkNewNavEnabled() && !checkIntentLandingPagesFeatureEnabled() ? '22px 24px' : 0,
      }}
    >
      <CompetenciesUploadSidePanel
        open={competencyUploadSidePanel}
        onCancel={closeCompetencyUploadSidePanel}
        onSuccess={openCompetencyUploadSidePanel}
        competencySettingsType={viewType}
      />
      {CompetenciesScreen}
    </div>
  );
};
const Container = styled.div`
  width: 100%;
  grid-template-columns: 1.5fr 2fr;
  min-height: 100vh;
  padding: ${() => (!checkNewNavEnabled() ? '16px' : 0)};
  ${(props) =>
    props.center &&
    css`
      text-align: center;
    `};
`;
export default Competencies;
